import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';
import api from '../services/api';
import '../styles/Analytics.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('monthly');
  const [error, setError] = useState('');

  useEffect(() => {
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.getAnalytics(period);
      if (response.data) {
        setAnalytics(response.data);
      }
      setError('');
    } catch (err) {
      setError('Failed to load analytics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading analytics...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!analytics) {
    return <div>No analytics data available</div>;
  }

  // Prepare chart data
  const enquiriesChartData = {
    labels: analytics.enquiry_timeline?.map((item) => item.date) || [],
    datasets: [
      {
        label: 'Enquiries',
        data: analytics.enquiry_timeline?.map((item) => item.count) || [],
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 2,
      },
    ],
  };

  const statusChartData = {
    labels: analytics.status_distribution?.map((item) => item.status) || [],
    datasets: [
      {
        data: analytics.status_distribution?.map((item) => item.count) || [],
        backgroundColor: [
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 99, 132, 0.6)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const conversionData = {
    labels: ['Total', 'Completed', 'In Progress', 'Pending', 'Rejected'],
    datasets: [
      {
        label: 'Count',
        data: [
          analytics.total_enquiries || 0,
          analytics.status_counts?.completed || 0,
          analytics.status_counts?.in_progress || 0,
          analytics.status_counts?.pending || 0,
          analytics.status_counts?.rejected || 0,
        ],
        backgroundColor: [
          'rgba(153, 102, 255, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(255, 159, 64, 0.6)',
          'rgba(255, 99, 132, 0.6)',
        ],
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  return (
    <div className="content-container">
      <div className="page-header-section">
        <h2 className="section-title">Analytics Dashboard</h2>
        <div className="header-actions">
          <div className="period-selector">
            <button
              className={`btn ${period === 'daily' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setPeriod('daily')}
            >
              Daily
            </button>
            <button
              className={`btn ${period === 'weekly' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setPeriod('weekly')}
            >
              Weekly
            </button>
            <button
              className={`btn ${period === 'monthly' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setPeriod('monthly')}
            >
              Monthly
            </button>
          </div>
          <button onClick={loadAnalytics} className="btn btn-secondary">
            Refresh
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Enquiries</h3>
          <p className="stat-value">{analytics.total_enquiries || 0}</p>
        </div>
        <div className="stat-card">
          <h3>Completed</h3>
          <p className="stat-value">{analytics.status_counts?.completed || 0}</p>
        </div>
        <div className="stat-card">
          <h3>In Progress</h3>
          <p className="stat-value">{analytics.status_counts?.in_progress || 0}</p>
        </div>
        <div className="stat-card">
          <h3>Pending</h3>
          <p className="stat-value">{analytics.status_counts?.pending || 0}</p>
        </div>
      </div>

      <div className="charts-grid">
        <div className="chart-card">
          <h3>Enquiries Timeline</h3>
          <div className="chart-container">
            <Line data={enquiriesChartData} options={chartOptions} />
          </div>
        </div>

        <div className="chart-card">
          <h3>Status Distribution</h3>
          <div className="chart-container">
            <Pie data={statusChartData} options={chartOptions} />
          </div>
        </div>

        <div className="chart-card full-width">
          <h3>Conversion Overview</h3>
          <div className="chart-container">
            <Bar data={conversionData} options={chartOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;

